/**
 * Solana "quick play" — the one-tap flow the user asked for: enable → play.
 *
 * enableQuickPlay() does BOTH steps with a single gesture:
 *   1. Delegate the Privy embedded wallet to Hupper's server signer (one popup).
 *   2. The BACKEND deposits the whole wallet balance (minus a fee buffer) into the
 *      escrow Vault via POST /api/session/solana/deposit — NO wallet popup.
 * After that, UserAccount.available is funded and the settler locks per match with
 * no further user signatures, so the user just plays.
 *
 * Auto-sweep: while quick play is enabled, the hook watches the wallet balance
 * and folds any NEW SOL (above the fee buffer) into the Vault on its own — so a
 * top-up made AFTER enabling is playable too, without another tap or popup.
 * The sweep RE-ATTEMPTS on every 15s poll tick while a surplus remains, so one
 * transient RPC/API failure can't leave quick play delegated-but-unfunded.
 *
 * The sweep is also registered in quickPlayStore so the enable flows (stake
 * screen gate, Settings) can fund the Vault immediately after delegation
 * instead of waiting for the next poll tick, and so their UIs can show the
 * deposit's progress/errors (sweeping/error flags).
 *
 * Withdraw stays user-signed (funds are never trapped). Reversible via revoke().
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useEnableSessionSigner } from "../auth/sessionSigner";
import { useAuth } from "./useAuth";
import { useSolUsdPrice } from "./useSolUsdPrice";
import { sessionApi } from "../auth/api";
import {
  registerQuickPlaySweeper,
  setQuickPlaySweeping,
  setQuickPlayError,
  bumpVaultDirty,
} from "../auth/quickPlayStore";

/** Target buffer left in the wallet on a sweep, in USD (see feeBuffer below). */
const FEE_BUFFER_USD = 0.2;
/** Hard floor for the buffer (lamports): the first deposit's UserAccount rent
 *  (~0.0012 SOL) + tx fee. $0.20 buys fewer lamports when SOL is pricey, so this
 *  floor keeps the deposit tx from failing above ~$130/SOL. */
const FEE_BUFFER_FLOOR_LAMPORTS = 1_500_000n; // 0.0015 SOL
/** Don't auto-sweep a surplus smaller than this — avoids churning dust top-ups. */
const MIN_SWEEP_LAMPORTS = 2_000_000n; // 0.002 SOL

export interface UseSolanaQuickPlay {
  ready: boolean; // env configured (server signer id present)
  enabled: boolean; // wallet already delegated
  busy: boolean;
  walletBalance: bigint | null; // native SOL in the embedded wallet
  error: string | null;
  enableQuickPlay: () => Promise<void>;
  revoke: () => Promise<void>;
}

export function useSolanaQuickPlay(walletAddress: string | null): UseSolanaQuickPlay {
  const session = useEnableSessionSigner();
  const { apiToken } = useAuth();
  const { price: solUsd } = useSolUsdPrice();
  const [walletBalance, setWalletBalance] = useState<bigint | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Serialises the initial enable-deposit, the auto-sweep poll and store-driven
  // sweep requests so they can't fire overlapping deposits (the backend
  // idempotencyKey dedupes any that slip).
  const sweepingRef = useRef(false);

  // Fee buffer left in the wallet on every sweep: ~$0.20 worth of SOL, but never
  // below the on-chain floor (rent + fee). The real cost is in lamports, not USD,
  // so we peg to $0.20 for a stable UX and clamp up to the floor when SOL is
  // expensive. Falls back to the floor until the price loads.
  const feeBuffer = useMemo<bigint>(() => {
    const usd = solUsd && solUsd > 0 ? BigInt(Math.ceil((FEE_BUFFER_USD / solUsd) * 1e9)) : 0n;
    return usd > FEE_BUFFER_FLOOR_LAMPORTS ? usd : FEE_BUFFER_FLOOR_LAMPORTS;
  }, [solUsd]);

  // Move the wallet's surplus SOL (balance − fee buffer) into the Vault via the
  // backend session signer (no popup). Reads a FRESH balance unless the caller
  // just polled one (prefetched) — deciding on stale state is how a sweep
  // no-ops right after funds arrive. Publishes progress + errors to
  // quickPlayStore so the gate UIs can show them, and bumps vault-dirty on
  // success so money hooks re-read `available` immediately.
  const sweepNow = useCallback(
    async (minLamports: bigint, prefetched?: bigint | null): Promise<void> => {
      if (!walletAddress || !apiToken || sweepingRef.current) return;
      sweepingRef.current = true;
      setBusy(true);
      try {
        let bal = prefetched ?? null;
        if (bal === null) {
          const { getWalletBalance } = await import("../contracts/solana/escrow");
          bal = await getWalletBalance(walletAddress).catch(() => null);
          if (bal !== null) setWalletBalance(bal);
        }
        const amount = bal !== null && bal > feeBuffer ? bal - feeBuffer : 0n;
        if (amount <= 0n || amount < minLamports) return;
        // Only flag the store once a REAL deposit starts — the no-op guard
        // path above runs every poll tick and must not flicker the gate UIs.
        setQuickPlaySweeping(true);
        await sessionApi.solanaDeposit(apiToken, amount.toString());
        setError(null);
        setQuickPlayError(null);
        bumpVaultDirty();
      } catch (e: any) {
        const msg = e?.message ?? "deposit failed";
        setError(msg);
        setQuickPlayError(msg);
        console.error("[quickplay] wallet→Vault sweep failed:", e);
      } finally {
        sweepingRef.current = false;
        setBusy(false);
        setQuickPlaySweeping(false);
      }
    },
    [walletAddress, apiToken, feeBuffer],
  );

  // Poll the embedded wallet's native SOL balance (what "deposit all" moves in)
  // and, while quick play is enabled, fold any above-buffer surplus into the
  // Vault on EVERY tick — this is both the auto-sweep and its retry loop.
  useEffect(() => {
    if (!walletAddress) {
      setWalletBalance(null);
      return;
    }
    let alive = true;
    const tick = async () => {
      const { getWalletBalance } = await import("../contracts/solana/escrow");
      const b = await getWalletBalance(walletAddress).catch(() => null);
      if (!alive) return;
      setWalletBalance(b);
      if (session.enabled) void sweepNow(MIN_SWEEP_LAMPORTS, b);
    };
    void tick();
    const t = setInterval(() => void tick(), 15_000);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, [walletAddress, session.enabled, sweepNow]);

  // Let the enable flows (gate / Settings) trigger an immediate deposit right
  // after delegation. 1n threshold: an explicit enable should move ANY surplus.
  useEffect(() => {
    if (!walletAddress) return;
    return registerQuickPlaySweeper(() => sweepNow(1n));
  }, [walletAddress, sweepNow]);

  const enableQuickPlay = useCallback(async () => {
    setError(null);
    setBusy(true);
    try {
      // Delegate (the only popup) — skip if already delegated. enable() throws
      // on failure (and delegateWallet only resolves after the user accepted),
      // so reaching the sweep means delegation is really in place.
      if (!session.enabled) await session.enable();
      if (!apiToken) throw new Error("not signed in");
      // Deposit whatever is in the wallet now (any surplus above the buffer);
      // the auto-sweep tick keeps the Vault topped up afterwards.
      await sweepNow(1n);
    } catch (e: any) {
      setError(e?.message ?? "quick play failed");
    } finally {
      setBusy(false);
    }
  }, [session, apiToken, sweepNow]);

  return {
    ready: session.ready,
    enabled: session.enabled,
    busy: busy || session.enabling,
    walletBalance,
    error: error ?? session.error,
    enableQuickPlay,
    revoke: session.revoke,
  };
}
